import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import {
    map,
    get,
    includes,
    join,
    sortBy,
    size,
    filter as lodashFilter,
} from "lodash";

import Grid from "@material-ui/core/Grid";

import CustomInput from "../CustomInput";
import CustomSelect from "../CustomSelect";
import MultiLevelSelect from "../MultiLevelSelect";
import TableCellStyled from "../TableCellStyled";
import SmartSearch from "../../Common/SmartSearch";

import { getAttributeName } from "../../../utils/attributes";

import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";
import SearchableMultipleSelect from "../SearchableMultipleSelect";

const styles = {
    headerCell: {
        backgroundColor: colors.cardTitleBg,
        borderRight: `3px solid ${colors.white}`,
        height: "50px",
        lineHeight: "1.3",
        boxSizing: "border-box",
    },
    headerIcon: {
        display: "inline-block",
        maxWidth: "40px",
        height: "20px",
        //marginRight: "10px",
        "& img": {
            maxWidth: "40px",
            maxHeight: "20px",
        },
    },
    input: {
        height: "36px",
        backgroundColor: colors.white,
        fontSize: "1em",
    },
};

const HeaderCell = (props) => {
    const { classes, filterValue, lov, language, isLast } = props;
    const { name, name2, id, minWidth, width, icon, filter } = props.column;

    const getRenderValue = () => {
        if (get(filter, ["type"]) === "comboBox") {
            return (selected) => (
                <span
                    style={{
                        whiteSpace: "nowrap",
                        lineHeight: "25px",
                        overflow: "hidden",
                    }}
                >
                    {join(
                        map(selected, (s) =>
                            get(
                                lodashFilter(
                                    map(lov, (item) => ({
                                        id: item.id,
                                        name: get(item, [`name_${language}`]),
                                    })),
                                    (opt) => get(opt, ["id"]) === s
                                ),
                                [0, "name"]
                            )
                        ),
                        ", "
                    )}
                </span>
            )
        }
        return (value) => {
            if (size(value) === 0) return null;
            if (size(value) === 1) {
                return get(
                    lodashFilter(
                        map(lov, (item) => ({
                            id: item.id,
                            name: get(item, [`name_${language}`]),
                        })),
                        (opt) => get(opt, ["id"]) === get(value, [0])
                    ),
                    [0, "name"]
                );
            }
            if (size(value) === size(lov))
                return I18n.get("common_all");
            return `(${size(value)} ${I18n.get("common_selected")})`;
        }
    }

    return (
        <TableCellStyled
            className={classes.headerCell}
            style={{
                minWidth: minWidth,
                width: width,
                maxWidth: width,
                borderRight: isLast ? "none" : "",
            }}
        >
            <Grid container direction="column">
                <Grid item>
                    <Grid
                        container
                        alignItems="center"
                        justify={icon && !name ? "center" : "flex-start"}
                    >
                        {icon && (
                            <Grid
                                item
                                component="span"
                                className={classes.headerIcon}
                                style={{
                                    marginRight: icon && !name ? 0 : 10,
                                }}
                            >
                                <img src={icon} alt="headerIcon" />
                            </Grid>
                        )}
                        <Grid item>
                            {id ? getAttributeName(id) : name2 ? name2 : I18n.get(name)}
                        </Grid>
                    </Grid>
                </Grid>
                {get(filter, ["type"]) === "searchableMultiple" && (
                    <Grid item style={{ maxWidth: "100%" }}>
                        <SearchableMultipleSelect
                            name={name}
                            value={filterValue || []}
                            options={map(
                                sortBy(lov, (item) => get(item, [`name_${language}`])),
                                (item) => ({
                                    id: item.id,
                                    name: get(item, [`name_${language}`]),
                                })
                            )}
                            onChange={props.handleFilterChange}
                            onClose={props.handleFilterClose}
                        />
                    </Grid>
                )}
                {get(filter, ["type"]) === "smartsearch" && (
                    <Grid item style={{ maxWidth: "100%" }}>
                        <SmartSearch
                            data={props.data}
                            activeFilter={filterValue}
                            filter={get(filter, ["path"])}
                            onSelect={props.handleFilterChange}
                            dataAutomation={get(filter, ["name"])}
                            inputClass={classes.input}
                            startAdornment={
                                <img
                                    src={images.searchIcon}
                                    alt="search"
                                    width="17px"
                                    height="17px"
                                />
                            }
                        />
                    </Grid>
                )}
                {get(filter, ["type"]) === "multilevelSelect" && (
                    <Grid item style={{ maxWidth: "100%" }}>
                        <MultiLevelSelect
                            name={name}
                            values={filterValue}
                            onChange={props.handleFilterChange}
                            options={lov}
                            onClose={props.handleFilterClose}
                        />
                    </Grid>
                )}
                {get(filter, ["type"]) === "input" && (
                    <Grid item style={{ maxWidth: "100%" }}>
                        <CustomInput
                            name={name}
                            value={filterValue || ""}
                            onChange={props.handleFilterChange}
                            dataAutomation={get(filter, ["name"])}
                            inputClass={classes.input}
                            startAdornment={
                                <img
                                    src={images.searchIcon}
                                    alt="search"
                                    width="17px"
                                    height="17px"
                                />
                            }
                        />
                    </Grid>
                )}
                {includes(["select", "comboBox"], get(filter, ["type"])) && (
                    <Grid item style={{ maxWidth: "100%" }}>
                        <CustomSelect
                            name={name}
                            value={filterValue || []}
                            options={map(
                                sortBy(lov, (item) => get(item, [`name_${language}`])),
                                (item) => ({
                                    id: item.id,
                                    name: get(item, [`name_${language}`]),
                                })
                            )}
                            onChange={props.handleFilterChange}
                            multiple
                            selectAll
                            clickThrough
                            onClose={props.handleFilterClose}
                            dataAutomation={get(filter, ["name"])}
                            comboBox={get(filter, ["type"]) === "comboBox"}
                            renderValue={getRenderValue()}
                        />
                    </Grid>
                )}
            </Grid>
        </TableCellStyled>
    );
};

HeaderCell.propTypes = {
    column: PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.number,
        minWidth: PropTypes.number,
    }).isRequired,
};

const mapStateToProps = (state) => {
    return {
        language: state.user.language,
    };
};

export default connect(mapStateToProps)(withStyles(styles)(HeaderCell));
